import { z } from 'zod';
import { uuid } from './fields';
import { TripItemEvent } from './trip-items';

/**
 * Live collaboration wire types — the per-trip SSE stream
 * (`GET /api/trips/:tripId/stream`) and the presence report
 * (`POST /api/trips/:tripId/presence`).
 *
 * The stream is one-way; everything a client learns arrives as one of the
 * frames below. Mutations and presence go up over the normal REST routes.
 */

/** First frame on every (re)connect. */
export const StreamHello = z.object({
  /** Identifies this connection in presence reports. */
  connectionId: z.string().min(1),
  /** Newest trip event id at connect time (the client's new cursor). */
  cursor: uuid().nullable(),
  /**
   * True when the client's `Last-Event-ID` was unknown, so nothing was replayed
   * and the client must refetch the trip instead of trusting its cache.
   */
  resync: z.boolean(),
});
export type StreamHello = z.infer<typeof StreamHello>;

/** A batch of trip events (newest first), pushed after any mutation on the trip. */
export const StreamEvents = z.object({
  events: z.array(TripItemEvent),
  /** The `x-client-id` of the tab that caused the batch, so it can skip its own echo. */
  origin: z.string().nullable(),
});
export type StreamEvents = z.infer<typeof StreamEvents>;

/** One person (one tab) currently on the trip's live stream. */
export const PresenceEntry = z.object({
  connectionId: z.string().min(1),
  userId: uuid(),
  email: z.string(),
  displayName: z.string().nullable(),
  /** The item whose details this tab has open, if any. */
  openItemId: uuid().nullable(),
  /** True while that tab has the item's Edit toggle on. */
  editing: z.boolean(),
});
export type PresenceEntry = z.infer<typeof PresenceEntry>;

export const StreamPresence = z.object({
  roster: z.array(PresenceEntry),
});
export type StreamPresence = z.infer<typeof StreamPresence>;

/** POST /api/trips/:tripId/presence — sent on change, not on a timer. */
export const PresenceInput = z.object({
  connectionId: z.string().min(1),
  openItemId: uuid().nullable(),
  editing: z.boolean(),
});
export type PresenceInput = z.infer<typeof PresenceInput>;
